import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import { logger } from "./logger";

// Buyer lifecycle stages for platform users
export type BuyerLifecycleStage = "prospect" | "onboarding" | "active" | "paused" | "churned";

export const BUYER_STAGE_OPTIONS: {
  value: BuyerLifecycleStage;
  label: string;
  description: string;
}[] = [
  { value: "prospect", label: "Prospect", description: "Lead, not yet onboarded" },
  { value: "onboarding", label: "Onboarding", description: "Setting up card program" },
  { value: "active", label: "Active", description: "Running campaigns, engaged" },
  { value: "paused", label: "Paused", description: "Temporarily inactive" },
  { value: "churned", label: "Churned", description: "Former customer" },
];

export type RelationshipType = "subsidiary" | "division" | "regional" | "holding";
export type EntityType = Database["public"]["Enums"]["entity_type"];
type CorporateInsert = Database["public"]["Tables"]["corporates"]["Insert"];
type CorporateUpdate = Database["public"]["Tables"]["corporates"]["Update"];

export interface Corporate {
  id: number;
  corporate_code?: string;
  name: string;
  legal_name?: string | null;
  website: string | null;
  domain?: string | null;
  phone: string | null;
  phone_e164?: string | null;
  mailing_address?: string | null;
  status: string;
  buyer_stage: BuyerLifecycleStage | null;
  buyer_type?: string | null;
  buyer_icp_score?: number | null;
  buyer_icp_tier?: string | null;
  buyer_icp_calculated_at?: string | null;
  revenue: string | null;
  annual_payment_volume: number | null;
  total_supplier_count: number | null;
  last_activity: string | null;
  avatar: string | null;
  is_platform_user: boolean;
  entity_type?: EntityType;
  industry: string | null;
  duns_number: string | null;
  ein?: string | null;
  tax_id: string | null;
  incorporation_country: string | null;
  business_type: string | null;
  ownership_type?: string | null;
  employee_count: number | null;
  annual_revenue_range: string | null;
  // Relationship fields
  parent_corporate_id: number | null;
  relationship_type: RelationshipType | null;
  relationship_notes: string | null;
  // Duplicate tracking
  duplicate_acknowledged: boolean;
  duplicate_note: string | null;
  annual_fraud_losses: number | null;
  logo_url: string | null;
  custom_task_domain: string | null;
  /** Stock ticker for a public company (GF-001 column). Drives the GuruFocus financials upload. */
  ticker_symbol?: string | null;
  email_from_name?: string | null;
  email_sender_name?: string | null;
  email_signature?: string | null;
  reply_to_email?: string | null;
  sender_email_prefix?: string | null;
  billing_enabled?: boolean | null;
  // H.2 HubSpot Company Demographic Profile fields
  sic_code: string | null;
  naics_code: string | null;
  mcc_code?: string | null;
  affiliated_company_count: number | null;
  us_based: boolean | null;
  supplier_business_category: string | null;
  // T4.1 Cal.com scheduling integration
  calcom_slug: string | null;
  // T3.1 AI Copilot feature flag
  ai_copilot_enabled: boolean;
  hygiene_status?: string | null;
  hygiene_version?: number | null;
  identity_confidence?: number | null;
  last_hygiene_run_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface CorporateWithFinancialData {
  id: number;
  corporate_code?: string | null;
  name: string;
  legal_name?: string | null;
  website: string | null;
  domain?: string | null;
  phone: string | null;
  phone_e164?: string | null;
  mailing_address?: string | null;
  status: string;
  buyer_stage: BuyerLifecycleStage | null;
  buyer_type?: string | null;
  buyer_icp_score?: number | null;
  buyer_icp_tier?: string | null;
  buyer_icp_calculated_at?: string | null;
  revenue: string | null;
  last_activity: string | null;
  avatar: string | null;
  is_platform_user: boolean;
  entity_type?: EntityType;
  industry: string | null;
  duns_number: string | null;
  ein?: string | null;
  tax_id: string | null;
  incorporation_country: string | null;
  business_type: string | null;
  ownership_type?: string | null;
  employee_count: number | null;
  annual_revenue_range: string | null;
  parent_corporate_id: number | null;
  relationship_type: string | null;
  relationship_notes: string | null;
  duplicate_acknowledged: boolean | null;
  duplicate_note: string | null;
  annual_fraud_losses: number | null;
  logo_url: string | null;
  custom_task_domain: string | null;
  /** Stock ticker for a public company (GF-001 column). Drives the GuruFocus financials upload. */
  ticker_symbol?: string | null;
  email_from_name?: string | null;
  email_sender_name?: string | null;
  email_signature?: string | null;
  reply_to_email?: string | null;
  sender_email_prefix?: string | null;
  billing_enabled?: boolean | null;
  // H.2 HubSpot Company Demographic Profile fields
  sic_code: string | null;
  naics_code: string | null;
  mcc_code?: string | null;
  affiliated_company_count: number | null;
  us_based: boolean | null;
  supplier_business_category: string | null;
  calcom_slug: string | null;
  ai_copilot_enabled: boolean;
  hygiene_status?: string | null;
  hygiene_version?: number | null;
  identity_confidence?: number | null;
  last_hygiene_run_at?: string | null;
  created_at: string;
  updated_at: string;
  financial_snapshot:
    | {
        total_current_volume: number;
        total_supplier_count: number;
        is_current: boolean;
      }[]
    | null
    | undefined;
}

type CorporateDetailsCommandResult =
  | { ok: true; corporate: CorporateWithFinancialData; request_id?: string | null }
  | { ok: false; code: string; message: string };

export class CorporateDetailsUpdateError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "CorporateDetailsUpdateError";
  }
}

function parseCorporateDetailsCommandResult(value: unknown): CorporateDetailsCommandResult {
  if (!value || typeof value !== "object") {
    throw new CorporateDetailsUpdateError(
      "invalid_response",
      "Corporate details command returned an invalid response",
    );
  }

  const result = value as Record<string, unknown>;
  if (
    result.ok === false &&
    typeof result.code === "string" &&
    typeof result.message === "string"
  ) {
    return result as CorporateDetailsCommandResult;
  }

  if (result.ok === true && result.corporate && typeof result.corporate === "object") {
    return result as CorporateDetailsCommandResult;
  }

  throw new CorporateDetailsUpdateError(
    "invalid_response",
    "Corporate details command returned an invalid response",
  );
}

function createCorporateDetailsRequestId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `corporate-details-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

const mapCorporateData = (item: CorporateWithFinancialData): Corporate => {
  // Find the current financial snapshot if it exists
  const currentSnapshot = item.financial_snapshot?.find((snapshot) => snapshot.is_current) || null;

  return {
    id: item.id,
    corporate_code: item.corporate_code ?? undefined,
    name: item.name,
    legal_name: item.legal_name ?? null,
    website: item.website,
    domain: item.domain ?? null,
    phone: item.phone,
    phone_e164: item.phone_e164 ?? null,
    mailing_address: item.mailing_address ?? null,
    status: item.status,
    buyer_stage: item.buyer_stage,
    buyer_type: item.buyer_type ?? null,
    buyer_icp_score: item.buyer_icp_score ?? null,
    buyer_icp_tier: item.buyer_icp_tier ?? null,
    buyer_icp_calculated_at: item.buyer_icp_calculated_at ?? null,
    revenue: item.revenue,
    annual_payment_volume: currentSnapshot?.total_current_volume || null,
    total_supplier_count: currentSnapshot?.total_supplier_count || null,
    last_activity: item.last_activity,
    avatar: item.avatar,
    is_platform_user: item.is_platform_user,
    entity_type: item.entity_type ?? "corporate",
    industry: item.industry,
    duns_number: item.duns_number,
    ein: item.ein ?? null,
    tax_id: item.tax_id,
    incorporation_country: item.incorporation_country,
    business_type: item.business_type,
    ownership_type: item.ownership_type ?? null,
    employee_count: item.employee_count,
    annual_revenue_range: item.annual_revenue_range,
    parent_corporate_id: item.parent_corporate_id || null,
    relationship_type: item.relationship_type as RelationshipType | null,
    relationship_notes: item.relationship_notes || null,
    duplicate_acknowledged: item.duplicate_acknowledged || false,
    duplicate_note: item.duplicate_note || null,
    annual_fraud_losses: item.annual_fraud_losses || null,
    logo_url: (item as unknown as { logo_url: string | null }).logo_url ?? null,
    custom_task_domain:
      (item as unknown as { custom_task_domain: string | null }).custom_task_domain ?? null,
    ticker_symbol: (item as unknown as { ticker_symbol?: string | null }).ticker_symbol ?? null,
    email_from_name: item.email_from_name ?? null,
    email_sender_name: item.email_sender_name ?? null,
    email_signature: item.email_signature ?? null,
    reply_to_email: item.reply_to_email ?? null,
    sender_email_prefix: item.sender_email_prefix ?? null,
    billing_enabled: item.billing_enabled ?? null,
    // H.2 HubSpot Company Demographic Profile fields
    sic_code: item.sic_code ?? null,
    naics_code: item.naics_code ?? null,
    mcc_code: item.mcc_code ?? null,
    affiliated_company_count: item.affiliated_company_count ?? null,
    us_based: item.us_based ?? null,
    supplier_business_category: item.supplier_business_category ?? null,
    calcom_slug: (item as unknown as { calcom_slug: string | null }).calcom_slug ?? null,
    ai_copilot_enabled:
      (item as unknown as { ai_copilot_enabled: boolean }).ai_copilot_enabled ?? false,
    hygiene_status: item.hygiene_status ?? null,
    hygiene_version: item.hygiene_version ?? null,
    identity_confidence: item.identity_confidence ?? null,
    last_hygiene_run_at: item.last_hygiene_run_at ?? null,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
};

/**
 * How many corporates a single search returns. R-41.
 *
 * Stated explicitly so truncation is a decision this code owns. PostgREST's own
 * default is also 1000, but inheriting it means no caller can distinguish "there
 * were exactly 1000" from "the server stopped counting" — which is precisely how
 * 137,388 corporates became invisible without an error.
 */
export const CORPORATE_SEARCH_LIMIT = 50;

export const corporatesService = {
  async getAll(): Promise<Corporate[]> {
    try {
      logger.info("Fetching all corporates");
      const { data, error } = await supabase
        .from("corporates")
        .select(
          `
          *,
          financial_snapshot:corporate_financial_snapshots!left(
            total_current_volume,
            total_supplier_count,
            is_current
          )
        `,
        )
        .order("name");

      if (error) {
        logger.error("Failed to fetch corporates", error);
        throw error;
      }

      logger.info("Successfully fetched corporates", { count: data?.length || 0 });
      return ((data as unknown as CorporateWithFinancialData[]) || []).map(mapCorporateData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.getAll", error);
      throw error;
    }
  },

  /**
   * Server-side corporate search. R-41.
   *
   * `getAll()` issues a `.select()` with no bound, so PostgREST applies its
   * default max-rows cap of 1000 SILENTLY — `error` is null and the array simply
   * stops. Any caller that then filters in the browser is searching 1000 rows and
   * telling the user it searched everything. Measured against live data: 137,388
   * of 138,388 corporates were unreachable that way, and `Z-Tech Central` — plainly
   * visible on `/corporates` — could not be found in the AP upload picker at all.
   *
   * So the term goes to the database, and the bound is stated here rather than
   * inherited. `hasMore` on the result lets a caller tell the truth about
   * truncation instead of pretending the list is complete.
   */
  async search(term: string, limit = CORPORATE_SEARCH_LIMIT): Promise<Corporate[]> {
    try {
      const trimmed = term.trim();
      logger.info("Searching corporates", { term: trimmed, limit });

      let query = supabase.from("corporates").select(`
          *,
          financial_snapshot:corporate_financial_snapshots!left(
            total_current_volume,
            total_supplier_count,
            is_current
          )
        `);

      if (trimmed) {
        // PostgREST's `or` is a comma-separated list inside parentheses, so a term
        // containing `,` `(` `)` or `.` could otherwise close the group and append
        // a predicate of the caller's choosing. Strip the separators and wrap the
        // remainder as a LIKE pattern.
        const safe = trimmed.replace(/[,()."\\]/g, " ").trim();
        if (safe) {
          query = query.or(`name.ilike.*${safe}*,website.ilike.*${safe}*,industry.ilike.*${safe}*`);
        }
      }

      const { data, error } = await query.order("name").limit(limit);

      if (error) {
        logger.error("Failed to search corporates", error);
        throw error;
      }

      const rows = (data as unknown as CorporateWithFinancialData[]) || [];
      logger.info("Corporate search complete", {
        count: rows.length,
        truncated: rows.length >= limit,
      });
      return rows.map(mapCorporateData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.search", error);
      throw error;
    }
  },

  async getPlatformUsers(): Promise<Corporate[]> {
    try {
      logger.info("Fetching platform user corporates");
      const { data, error } = await supabase
        .from("corporates")
        .select(
          `
          *,
          financial_snapshot:corporate_financial_snapshots!left(
            total_current_volume,
            total_supplier_count,
            is_current
          )
        `,
        )
        .eq("is_platform_user", true)
        .order("name");

      if (error) {
        logger.error("Failed to fetch platform user corporates", error);
        throw error;
      }

      logger.info("Successfully fetched platform user corporates", { count: data?.length || 0 });
      return ((data as unknown as CorporateWithFinancialData[]) || []).map(mapCorporateData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.getPlatformUsers", error);
      throw error;
    }
  },

  async getById(id: number): Promise<Corporate | null> {
    try {
      logger.info("Fetching corporate by ID", { id });
      const { data, error } = await supabase
        .from("corporates")
        .select(
          `
          *,
          financial_snapshot:corporate_financial_snapshots!left(
            total_current_volume,
            total_supplier_count,
            is_current
          )
        `,
        )
        .eq("id", id)
        .single();

      if (error) {
        logger.error("Failed to fetch corporate", error, { id });
        throw error;
      }

      if (!data) return null;

      logger.info("Successfully fetched corporate by ID", { id });
      return mapCorporateData(data as unknown as CorporateWithFinancialData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.getById", error, { id });
      throw error;
    }
  },

  async create(corporate: Omit<Corporate, "id" | "created_at" | "updated_at">): Promise<Corporate> {
    try {
      logger.info("Creating corporate", { name: corporate.name });
      const insertPayload: CorporateInsert = {
        name: corporate.name,
        website: corporate.website,
        phone: corporate.phone,
        status: corporate.status,
        buyer_stage: corporate.buyer_stage,
        revenue: corporate.revenue,
        last_activity: corporate.last_activity,
        avatar: corporate.avatar,
        is_platform_user: corporate.is_platform_user,
        industry: corporate.industry,
        duns_number: corporate.duns_number,
        tax_id: corporate.tax_id,
        incorporation_country: corporate.incorporation_country,
        business_type: corporate.business_type,
        employee_count: corporate.employee_count,
        annual_revenue_range: corporate.annual_revenue_range,
        parent_corporate_id: corporate.parent_corporate_id,
        relationship_type: corporate.relationship_type,
        relationship_notes: corporate.relationship_notes,
        duplicate_acknowledged: corporate.duplicate_acknowledged,
        duplicate_note: corporate.duplicate_note,
        annual_fraud_losses: corporate.annual_fraud_losses,
        logo_url: corporate.logo_url,
        custom_task_domain: corporate.custom_task_domain,
        ticker_symbol: corporate.ticker_symbol ?? null,
        sic_code: corporate.sic_code,
        naics_code: corporate.naics_code,
        affiliated_company_count: corporate.affiliated_company_count,
        us_based: corporate.us_based,
        supplier_business_category: corporate.supplier_business_category,
        calcom_slug: corporate.calcom_slug,
        ai_copilot_enabled: corporate.ai_copilot_enabled,
        ...(corporate.legal_name !== undefined && { legal_name: corporate.legal_name }),
        ...(corporate.domain !== undefined && { domain: corporate.domain }),
        ...(corporate.phone_e164 !== undefined && { phone_e164: corporate.phone_e164 }),
        ...(corporate.mailing_address !== undefined && {
          mailing_address: corporate.mailing_address,
        }),
        ...(corporate.buyer_type !== undefined && { buyer_type: corporate.buyer_type }),
        ...(corporate.entity_type != null && { entity_type: corporate.entity_type }),
        ...(corporate.ein !== undefined && { ein: corporate.ein }),
        ...(corporate.ownership_type !== undefined && { ownership_type: corporate.ownership_type }),
        ...(corporate.email_from_name !== undefined && {
          email_from_name: corporate.email_from_name,
        }),
        ...(corporate.email_sender_name !== undefined && {
          email_sender_name: corporate.email_sender_name,
        }),
        ...(corporate.email_signature !== undefined && {
          email_signature: corporate.email_signature,
        }),
        ...(corporate.reply_to_email !== undefined && { reply_to_email: corporate.reply_to_email }),
        ...(corporate.sender_email_prefix !== undefined && {
          sender_email_prefix: corporate.sender_email_prefix,
        }),
        ...(corporate.mcc_code !== undefined && { mcc_code: corporate.mcc_code }),
      };

      const { data, error } = await supabase
        .from("corporates")
        .insert(insertPayload)
        .select()
        .single();

      if (error) {
        logger.error("Failed to create corporate", error, { name: corporate.name });
        throw error;
      }

      return mapCorporateData(data as unknown as CorporateWithFinancialData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.create", error, { name: corporate.name });
      throw error;
    }
  },

  async update(
    id: number,
    updates: Partial<Omit<Corporate, "id" | "created_at" | "updated_at">>,
  ): Promise<Corporate> {
    try {
      const updatePayload: CorporateUpdate = {
        ...(updates.status !== undefined && { status: updates.status }),
        ...(updates.buyer_stage !== undefined && { buyer_stage: updates.buyer_stage }),
        ...(updates.buyer_type !== undefined && { buyer_type: updates.buyer_type }),
        ...(updates.buyer_icp_score !== undefined && { buyer_icp_score: updates.buyer_icp_score }),
        ...(updates.buyer_icp_tier !== undefined && { buyer_icp_tier: updates.buyer_icp_tier }),
        ...(updates.buyer_icp_calculated_at !== undefined && {
          buyer_icp_calculated_at: updates.buyer_icp_calculated_at,
        }),
        ...(updates.revenue !== undefined && { revenue: updates.revenue }),
        ...(updates.last_activity !== undefined && { last_activity: updates.last_activity }),
        ...(updates.avatar !== undefined && { avatar: updates.avatar }),
        ...(updates.industry !== undefined && { industry: updates.industry }),
        ...(updates.business_type !== undefined && { business_type: updates.business_type }),
        ...(updates.ownership_type !== undefined && { ownership_type: updates.ownership_type }),
        ...(updates.employee_count !== undefined && { employee_count: updates.employee_count }),
        ...(updates.annual_revenue_range !== undefined && {
          annual_revenue_range: updates.annual_revenue_range,
        }),
        ...(updates.annual_fraud_losses !== undefined && {
          annual_fraud_losses: updates.annual_fraud_losses,
        }),
        ...(updates.logo_url !== undefined && { logo_url: updates.logo_url }),
        ...(updates.calcom_slug !== undefined && { calcom_slug: updates.calcom_slug }),
        ...(updates.hygiene_status != null && { hygiene_status: updates.hygiene_status }),
        ...(updates.hygiene_version != null && { hygiene_version: updates.hygiene_version }),
        ...(updates.identity_confidence !== undefined && {
          identity_confidence: updates.identity_confidence,
        }),
        ...(updates.last_hygiene_run_at !== undefined && {
          last_hygiene_run_at: updates.last_hygiene_run_at,
        }),
      };

      if (Object.keys(updatePayload).length === 0) {
        throw new Error(
          "No raw corporate fields to update; use updateDetailsWithAudit for Corporate Details fields",
        );
      }

      logger.info("Updating corporate", { id, fields: Object.keys(updatePayload) });

      const { data, error } = await supabase
        .from("corporates")
        .update(updatePayload)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        logger.error("Failed to update corporate", error, { id });
        throw error;
      }

      return mapCorporateData(data as unknown as CorporateWithFinancialData);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.update", error, { id });
      throw error;
    }
  },

  async updateDetailsWithAudit(
    id: number,
    updates: Partial<Omit<Corporate, "id" | "created_at" | "updated_at">>,
    expectedUpdatedAt?: string | null,
  ): Promise<Corporate> {
    try {
      logger.info("Updating corporate details through audited RPC", {
        id,
        fields: Object.keys(updates),
        expectedUpdatedAt,
      });

      const { data, error } = await supabase.rpc("update_corporate_details", {
        p_corporate_id: id,
        p_patch: updates as unknown as Json,
        // `p_expected_updated_at?: string` is OPTIONAL, not nullable (types.ts:27951),
        // so `?? null` was a type error rather than a runtime one. Omitting the
        // argument is what "no expectation" means to the SQL default.
        p_expected_updated_at: expectedUpdatedAt ?? undefined,
        p_request_id: createCorporateDetailsRequestId(),
      });

      if (error) {
        logger.error("Failed to update corporate details", error, { id });
        throw error;
      }

      const result = parseCorporateDetailsCommandResult(data);
      if (!result.ok) {
        throw new CorporateDetailsUpdateError(result.code, result.message);
      }

      return mapCorporateData(result.corporate);
    } catch (error: unknown) {
      logger.error("Error in corporatesService.updateDetailsWithAudit", error, { id });
      throw error;
    }
  },

  async delete(id: number): Promise<void> {
    try {
      logger.info("Deleting corporate", { id });
      const { error } = await supabase.from("corporates").delete().eq("id", id);

      if (error) {
        logger.error("Failed to delete corporate", error, { id });
        throw error;
      }
    } catch (error: unknown) {
      logger.error("Error in corporatesService.delete", error, { id });
      throw error;
    }
  },
};
